import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PeopleIcon from "@mui/icons-material/People";
import KanbanColumn from "./KanbanColumn";
import KanbanCard from "./KanbanCard";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: 16,
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
  },
  header: {
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  filterChips: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
    flex: 1,
  },
  board: {
    display: "flex",
    gap: 16,
    flex: 1,
    overflowX: "auto",
    alignItems: "flex-start",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
  modeButton: {
    textTransform: "none",
    fontSize: 12,
    padding: "4px 12px",
  },
}));

const RESOLVIDOS_ID = "resolvidos";

const Kanban = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isPrivileged = user.profile === "admin" || user.profile === "supervisor";

  const [viewMode, setViewMode] = useState("queue"); // "queue" | "user"
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [hidden, setHidden] = useState(new Set()); // hidden queue or user IDs
  const [showResolvidos, setShowResolvidos] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Fetch reference data once
  useEffect(() => {
    Promise.all([
      api.get("/queue"),
      api.get("/users"),
    ]).then(([qRes, uRes]) => {
      setQueues(qRes.data || []);
      setUsers((uRes.data?.users || uRes.data || []).filter((u) => u.profile !== "admin" || isPrivileged));
    }).catch(toastError);
  }, [isPrivileged]);

  // Fetch tickets
  useEffect(() => {
    setLoading(true);
    const fetchAllPages = async (status, showAll) => {
      const results = [];
      let page = 1;
      while (true) {
        const res = await api.get("/tickets", { params: { status, showAll, pageNumber: page } });
        results.push(...(res.data.tickets || []));
        if (!res.data.hasMore || page >= 20) break;
        page++;
      }
      return results;
    };
    const fetchTickets = async () => {
      try {
        const [pending, open] = await Promise.all([
          fetchAllPages("pending", "true"),
          fetchAllPages("open", isPrivileged ? "true" : "false"),
        ]);
        setTickets([...pending, ...open]);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [isPrivileged]);

  // Load/unload closed tickets
  useEffect(() => {
    if (!showResolvidos) {
      setTickets((prev) => prev.filter((t) => t.status !== "closed"));
      return;
    }
    const fetchClosed = async () => {
      const results = [];
      let page = 1;
      while (true) {
        const res = await api.get("/tickets", {
          params: { status: "closed", showAll: isPrivileged ? "true" : "false", pageNumber: page },
        });
        results.push(...(res.data.tickets || []));
        if (!res.data.hasMore || page >= 10) break;
        page++;
      }
      setTickets((prev) => [...prev.filter((t) => t.status !== "closed"), ...results]);
    };
    fetchClosed().catch(() => {});
  }, [showResolvidos, isPrivileged]);

  // Socket
  useEffect(() => {
    const socket = openSocket();
    const joinRooms = () => {
      socket.emit("joinTickets", "pending");
      socket.emit("joinTickets", "open");
      if (showResolvidos) socket.emit("joinTickets", "closed");
    };
    if (socket.connected) joinRooms();
    socket.on("connect", joinRooms);
    socket.on("ticket", (data) => {
      if (data.action === "update" && data.ticket) {
        const t = data.ticket;
        const visible =
          isPrivileged ||
          t.status === "pending" ||
          (t.status === "closed" && showResolvidos) ||
          (t.status === "open" && t.userId === user.id);
        setTickets((prev) => {
          const exists = prev.find((x) => x.id === t.id);
          if (!visible) return exists ? prev.filter((x) => x.id !== t.id) : prev;
          return exists
            ? prev.map((x) => (x.id === t.id ? { ...x, ...t } : x))
            : [t, ...prev];
        });
      }
      if (data.action === "delete" && data.ticketId) {
        setTickets((prev) => prev.filter((x) => x.id !== data.ticketId));
      }
    });
    return () => {
      socket.off("connect", joinRooms);
      socket.off("ticket");
      socket.disconnect();
    };
  }, [isPrivileged, user.id, showResolvidos]);

  // Build columns based on viewMode
  const columns = useMemo(() => {
    if (viewMode === "queue") {
      return [
        ...queues
          .filter((q) => !hidden.has(q.id))
          .map((q) => ({ id: `q-${q.id}`, foreignId: q.id, title: q.name, color: q.color })),
        { id: "sem-ref", foreignId: null, title: "Sem Fila", color: "#8A9096" },
      ];
    }
    // user mode
    return [
      ...users
        .filter((u) => !hidden.has(u.id))
        .map((u) => ({
          id: `u-${u.id}`,
          foreignId: u.id,
          title: u.name,
          color: "#273A74",
          avatar: u.name?.[0]?.toUpperCase(),
        })),
      { id: "sem-ref", foreignId: null, title: "Sem Atendente", color: "#8A9096" },
    ];
  }, [viewMode, queues, users, hidden]);

  const filterItems = viewMode === "queue" ? queues : users;

  const getColumnTickets = (foreignId) => {
    if (viewMode === "queue") {
      return tickets.filter(
        (t) => (t.queueId ?? null) === foreignId && t.status !== "closed"
      );
    }
    return tickets.filter(
      (t) => (t.userId ?? null) === foreignId && t.status !== "closed"
    );
  };

  const resolvidosTickets = tickets.filter((t) => t.status === "closed");

  const toggleItem = (id) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleModeChange = (mode) => {
    setViewMode(mode);
    setHidden(new Set());
  };

  const handleDragStart = ({ active }) => {
    setActiveTicket(tickets.find((t) => t.id === active.id) || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveTicket(null);
    if (!over) return;

    const dragged = tickets.find((t) => t.id === active.id);
    if (!dragged) return;

    // Resolve target: column or card
    const overCol = [...columns, { id: RESOLVIDOS_ID }].find((c) => c.id === over.id);
    let newForeignId;
    if (overCol) {
      if (overCol.id === RESOLVIDOS_ID) return; // read-only
      newForeignId = overCol.foreignId;
    } else {
      const overTicket = tickets.find((t) => t.id === over.id);
      if (!overTicket) return;
      newForeignId = viewMode === "queue"
        ? (overTicket.queueId ?? null)
        : (overTicket.userId ?? null);
    }

    const currentId = viewMode === "queue"
      ? (dragged.queueId ?? null)
      : (dragged.userId ?? null);

    if (currentId === newForeignId) return;

    // Optimistic
    const patch = viewMode === "queue"
      ? { queueId: newForeignId }
      : { userId: newForeignId, status: newForeignId ? "open" : "pending" };

    setTickets((prev) =>
      prev.map((t) => (t.id === dragged.id ? { ...t, ...patch } : t))
    );

    try {
      await api.put(`/tickets/${dragged.id}`, patch);
    } catch (err) {
      toastError(err);
      setTickets((prev) =>
        prev.map((t) =>
          t.id === dragged.id
            ? { ...t, queueId: dragged.queueId, userId: dragged.userId, status: dragged.status }
            : t
        )
      );
    }
  };

  const itemColor = (item) =>
    viewMode === "queue" ? item.color : "#273A74";

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>Kanban</Typography>

        <ButtonGroup size="small" variant="outlined">
          <Button
            className={classes.modeButton}
            variant={viewMode === "queue" ? "contained" : "outlined"}
            color={viewMode === "queue" ? "primary" : "default"}
            startIcon={<AccountTreeIcon style={{ fontSize: 15 }} />}
            onClick={() => handleModeChange("queue")}
          >
            Por Fila
          </Button>
          <Button
            className={classes.modeButton}
            variant={viewMode === "user" ? "contained" : "outlined"}
            color={viewMode === "user" ? "primary" : "default"}
            startIcon={<PeopleIcon style={{ fontSize: 15 }} />}
            onClick={() => handleModeChange("user")}
          >
            Por Usuário
          </Button>
        </ButtonGroup>

        <div className={classes.filterChips}>
          {filterItems.map((item) => {
            const active = !hidden.has(item.id);
            const color = itemColor(item);
            return (
              <Chip
                key={item.id}
                size="small"
                label={item.name}
                onClick={() => toggleItem(item.id)}
                style={{
                  backgroundColor: active ? color : "transparent",
                  color: active ? "#fff" : color,
                  border: `1.5px solid ${color}`,
                  fontWeight: 600,
                  fontSize: 11,
                  cursor: "pointer",
                }}
              />
            );
          })}
          <Chip
            size="small"
            label="Resolvidos"
            onClick={() => setShowResolvidos((v) => !v)}
            style={{
              backgroundColor: showResolvidos ? "#555A5F" : "transparent",
              color: showResolvidos ? "#fff" : "#555A5F",
              border: "1.5px solid #555A5F",
              fontWeight: 600,
              fontSize: 11,
              cursor: "pointer",
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress style={{ color: "#F37021" }} />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={classes.board}>
            {columns.map((col) => (
              <KanbanColumn
                key={col.id}
                id={col.id}
                title={col.title}
                color={col.color}
                tickets={getColumnTickets(col.foreignId)}
              />
            ))}
            {showResolvidos && (
              <KanbanColumn
                key={RESOLVIDOS_ID}
                id={RESOLVIDOS_ID}
                title="Resolvidos"
                color="#555A5F"
                tickets={resolvidosTickets}
                readOnly
              />
            )}
          </div>
          <DragOverlay>
            {activeTicket ? <KanbanCard ticket={activeTicket} /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default Kanban;
